import { useState, useEffect, useCallback } from 'react';
import { Bell, Sun, Moon, WifiOff, X, FileText, UserPlus, CheckCircle, AlertTriangle, CreditCard, MessageSquare, Globe, Copy, Link2 } from 'lucide-react';
import { ChatLauncher } from './ChatPanel';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n/index.jsx';
import api from '../utils/api';
import { PORTAL_CONFIG } from './Sidebar';

const NOTIF_ICONS = {
  new_request: { icon: FileText, color: '#F59E0B' },
  registration: { icon: UserPlus, color: '#8B5CF6' },
  approved: { icon: CheckCircle, color: '#10B981' },
  rejected: { icon: AlertTriangle, color: '#EF4444' },
  quote_signed: { icon: CreditCard, color: '#3B82F6' },
  docs_request: { icon: MessageSquare, color: '#F97316' },
  info: { icon: Bell, color: 'var(--text-muted)' },
};

export default function TopBar({ section, onNavigate }) {
  const { user, online } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang } = useI18n();
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [toast, setToast] = useState(null);
  const [prevCount, setPrevCount] = useState(0);

  const portal = PORTAL_CONFIG[user?.role];
  const sectionLabel = portal?.sections.find(s => s.id === section)?.label || 'Dashboard';

  const loadNotifs = useCallback(() => {
    if (!user) return;
    api.notifications().then(n => {
      const arr = Array.isArray(n) ? n : [];
      setNotifs(arr);
      const newUnread = arr.filter(x => !x.is_read).length;
      if (newUnread > prevCount && prevCount > 0) {
        const latest = arr.find(x => !x.is_read);
        if (latest) setToast({ title: latest.title, message: latest.message, type: latest.type });
      }
      setPrevCount(newUnread);
    }).catch(() => {});
  }, [user, prevCount]);

  useEffect(() => { loadNotifs(); }, [user, section]);
  // Poll every 30s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(loadNotifs, 30000);
    return () => clearInterval(interval);
  }, [user, loadNotifs]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t); }
  }, [toast]);

  const unread = notifs.filter(n => !n.is_read).length;

  const timeAgo = (date) => {
    if (!date) return '';
    const diff = (Date.now() - new Date(date).getTime()) / 1000;
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `${Math.floor(diff/60)}min`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h`;
    return `${Math.floor(diff/86400)}d`;
  };

  return (
    <>
      <header className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {user?.tenant_logo && (
            <img
              src={user.tenant_logo}
              alt=""
              style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8, background: '#fff', padding: 4, border: '1px solid var(--border)', flexShrink: 0 }}
            />
          )}
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sectionLabel}</h1>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {portal?.label} · {user?.tenant_name || 'MedCover Pro'}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {user?.role === 'admin' && user?.tenant_slug && <PortalLinkIndicator slug={user.tenant_slug} />}
          {!online && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#EF4444' }}>
              <WifiOff size={14} /> Sin conexión
            </div>
          )}
          <button onClick={() => setLang(lang === 'es' ? 'en' : 'es')} title={lang === 'es' ? 'Switch to English' : 'Cambiar a Español'} style={{ background: 'none', border: 'none', padding: 6, borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
            <Globe size={16} /> {lang.toUpperCase()}
          </button>
          <button onClick={toggleTheme} title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'} style={{ background: 'none', border: 'none', padding: 6, borderRadius: 6, color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
            {theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
          </button>
          <ChatLauncher />
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowNotifs(!showNotifs)} style={{ background: unread > 0 ? 'rgba(245,158,11,0.1)' : 'none', border: 'none', padding: 6, borderRadius: 8, color: unread > 0 ? '#F59E0B' : 'var(--text-muted)', cursor: 'pointer', position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Bell size={20} />
              {unread > 0 && (
                <span style={{ position: 'absolute', top: 0, right: 0, minWidth: 16, height: 16, borderRadius: 8, background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{unread > 9 ? '9+' : unread}</span>
              )}
            </button>
            {showNotifs && (
              <>
                <div onClick={() => setShowNotifs(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, width: 360, background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.15)', zIndex: 100, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 15, fontWeight: 700 }}>Notificaciones {unread > 0 && <span style={{ fontSize: 12, color: '#EF4444', fontWeight: 800 }}>({unread})</span>}</span>
                    {unread > 0 && (
                      <button onClick={async () => { await api.readAllNotifs(); setNotifs(n => n.map(x => ({...x, read: 1}))); setPrevCount(0); }}
                        style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                        Marcar leídas
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 380, overflow: 'auto' }}>
                    {notifs.length === 0 ? (
                      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                        <Bell size={24} style={{ marginBottom: 8, opacity: 0.3 }} /><br />Sin notificaciones
                      </div>
                    ) : notifs.slice(0, 20).map(n => {
                      const cfg = NOTIF_ICONS[n.type] || NOTIF_ICONS.info;
                      const Icon = cfg.icon;
                      const handleClick = async () => {
                        // Mark as read instantly
                        if (!n.is_read) {
                          api.readNotif(n.id);
                          setNotifs(prev => prev.map(x => x.id === n.id ? { ...x, is_read: 1 } : x));
                          setPrevCount(p => Math.max(0, p - 1));
                        }
                        // Navigate to linked section
                        if (n.link && onNavigate) {
                          const [sec, id] = n.link.split(':');
                          onNavigate(sec);
                          setShowNotifs(false);
                        }
                      };
                      return (
                        <div key={n.id} onClick={handleClick} style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, alignItems: 'flex-start', background: n.is_read ? 'transparent' : `${cfg.color}08`, cursor: n.link ? 'pointer' : 'default', transition: 'background 0.15s' }}
                          onMouseEnter={e => { if (!n.is_read) e.currentTarget.style.background = `${cfg.color}14`; }}
                          onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? 'transparent' : `${cfg.color}08`; }}>
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: `${cfg.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon size={15} color={cfg.color} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, marginBottom: 2 }}>{n.title}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.3 }}>{n.message}</div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                            <span style={{ fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{timeAgo(n.created_at)}</span>
                            {!n.is_read && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444' }} />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Toast notification */}
      {toast && (
        <div className="fade-in" style={{ position: 'fixed', top: 16, right: 16, zIndex: 1000, maxWidth: 360, padding: '12px 16px', background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.12)', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 28, height: 28, borderRadius: 6, background: `${(NOTIF_ICONS[toast.type] || NOTIF_ICONS.info).color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {(() => { const I = (NOTIF_ICONS[toast.type] || NOTIF_ICONS.info).icon; return <I size={14} color={(NOTIF_ICONS[toast.type] || NOTIF_ICONS.info).color} />; })()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{toast.title}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{toast.message}</div>
          </div>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', padding: 2 }}><X size={14} /></button>
        </div>
      )}
    </>
  );
}

/**
 * Portal link indicator — shows the tenant's public access URL with copy button.
 * Only visible for admins. Helps discoverability of /access/:slug for onboarding
 * clients and professionals.
 */
function PortalLinkIndicator({ slug }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const url = `https://thmedcoverpro.com/access/${slug}`;
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div
      onClick={() => setExpanded(!expanded)}
      className="portal-link-indicator"
      title="Link del portal público para clientes y profesionales"
      style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '5px 10px', background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
        border: '1px solid color-mix(in srgb, var(--accent) 30%, transparent)',
        borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--accent)',
        cursor: 'pointer', userSelect: 'none', maxWidth: expanded ? 320 : 180,
        transition: 'max-width 0.2s ease',
      }}
    >
      <Link2 size={13} style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
        {expanded ? url : `/access/${slug}`}
      </span>
      <button
        onClick={copy}
        title={copied ? '¡Copiado!' : 'Copiar link'}
        style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: copied ? '#10B981' : 'var(--accent)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
      >
        {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
      </button>
    </div>
  );
}
